import { useState, useCallback, useMemo } from 'react';

/**
 * Hook for managing table pagination state.
 * Replaces the common pattern of page/rowsPerPage state + handlers + data slicing.
 *
 * @param {object} [options]
 * @param {number} [options.defaultPage=0] - Initial page (0-indexed)
 * @param {number} [options.defaultRowsPerPage=10] - Initial rows per page
 * @param {number[]} [options.rowsPerPageOptions=[5, 10, 25, 50]] - Available page size options
 * @returns {object} Pagination state and handlers
 */
const usePagination = (options = {}) => {
  const {
    defaultPage = 0,
    defaultRowsPerPage = 10,
    rowsPerPageOptions = [5, 10, 25, 50],
  } = options;

  const [page, setPage] = useState(defaultPage);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  /** Reset to first page (useful when filters change) */
  const resetPage = useCallback(() => {
    setPage(0);
  }, []);

  /**
   * Slice an array for the current page.
   * @param {Array} data - Full data array
   * @returns {Array} Sliced data for current page
   */
  const paginateData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage]);

  /**
   * Props to spread directly onto MUI TablePagination.
   * Usage: <TablePagination {...pagination.tableProps(data.length)} />
   */
  const tableProps = useCallback((totalCount) => ({
    rowsPerPageOptions,
    component: 'div',
    count: totalCount,
    rowsPerPage,
    page,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
  }), [rowsPerPageOptions, rowsPerPage, page, handleChangePage, handleChangeRowsPerPage]);

  return useMemo(() => ({
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    resetPage,
    paginateData,
    tableProps,
  }), [page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, resetPage, paginateData, tableProps]);
};

export default usePagination;
